import React, { useEffect, useState } from "react";
import {
  ensureSpreadsheet,
  isSpreadsheetOwnedByMe,
  readMembers,
  spreadsheetUrl,
  writeMembers,
} from "../api/sheets";
import {
  cachedRootFolderUrl,
  ensureAssetsFolder,
  ensureFormsFolder,
  ensureMapsFolder,
  ensurePicturesFolder,
  revokeFileAccess,
  shareFileWithUser,
} from "../api/drive";
import { getUserEmail } from "../auth/googleAuth";
import { Member } from "../types/sheets";
import { useViewWidth } from "../components/ViewWidthSlider";
import { useT } from "../i18n";

const normEmail = (e: string) => e.trim().toLowerCase();

export default function MembersView() {
  const t = useT();
  const { maxWidth, slider } = useViewWidth("viewWidth.members");
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(true);
  const [snapshot, setSnapshot] = useState<Member[] | null>(null);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  // Set to the MewMaps root folder URL when the user is asked to share it by hand in Drive.
  const [driveShareUrl, setDriveShareUrl] = useState<string | null>(null);
  const myEmail = getUserEmail();
  const notify = (text: string, tone = "success") => { setStatus(text); setStatusTone(tone); };
  const notifyError = (text: string) => { setStatus(text); setStatusTone("error"); };

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const sid = await ensureSpreadsheet();
        setSpreadsheetId(sid);
        setMembers(await readMembers(sid));
        // Only the owner holds the drive.file grant on the root folder, so only they can
        // push/revoke Drive shares from here. Non-owner managers can still edit roles.
        setIsOwner(await isSpreadsheetOwnedByMe(sid));
      } catch (e) {
        notifyError(`${t("common.error")}: ${(e as Error).message}`);
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateEmail = (i: number, v: string) =>
    setMembers((rows) => rows.map((m, idx) => (idx === i ? { ...m, email: v } : m)));
  const updateRole = (i: number, v: Member["role"]) =>
    setMembers((rows) => rows.map((m, idx) => (idx === i ? { ...m, role: v } : m)));
  const addRow = () => setMembers((rows) => [...rows, { email: "", role: "fieldworker" }]);
  const removeRow = (i: number) => setMembers((rows) => rows.filter((_, idx) => idx !== i));

  function startEdit() {
    if (!window.confirm(t("members.confirmEdit"))) return;
    setSnapshot(members);
    setLocked(false);
  }

  function cancelEdit() {
    if (snapshot) setMembers(snapshot);
    setSnapshot(null);
    setLocked(true);
  }

  async function handleSave() {
    if (!spreadsheetId || locked) return;
    if (!window.confirm(t("members.confirmSave"))) return;
    setBusy(true);
    setDriveShareUrl(null);
    try {
      const before = new Set((snapshot || []).map((m) => normEmail(m.email)).filter(Boolean));
      const after = new Set(members.map((m) => normEmail(m.email)).filter(Boolean));
      const added = Array.from(after).filter((e) => !before.has(e));
      const removed = Array.from(before).filter((e) => !after.has(e));

      await writeMembers(spreadsheetId, members);

      // The owner also pushes/revokes Drive access on the root folder (children inherit), so
      // adding a member here grants access without a trip to Google Drive.
      const shareFailures: { email: string; message: string }[] = [];
      let manualShareNeeded = false;
      if (added.length || removed.length) {
        // The data spreadsheet is a single file (no children to cascade to), so this share
        // always works and is the one essential resource for "Join a shared project". It
        // carries the invite email. A hard failure here is a real error worth surfacing.
        for (const email of added) {
          try {
            await shareFileWithUser(spreadsheetId, email, { notify: true, emailMessage: t("members.inviteMessage") });
          } catch (e) {
            const message = (e as Error).message || String(e);
            console.error(`Failed to share the data sheet with ${email}:`, e);
            shareFailures.push({ email, message });
          }
        }
        for (const email of removed) {
          try { await revokeFileAccess(spreadsheetId, email); } catch { /* best-effort revoke */ }
        }

        // Folders (pictures/maps/assets/forms) are shared INDEPENDENTLY and best-effort.
        // Sharing a folder cascades the grant to its children, which Drive refuses under
        // drive.file (appNotAuthorizedToChild) if the folder holds a file the app didn't
        // create — e.g. a manually-linked form-responses sheet that landed in one of them.
        // The owner's app created most children so this usually succeeds; whatever can't be
        // auto-shared falls back to a "share the folder manually in Drive" note. A non-owner
        // manager isn't authorized to the folders' children at all, so we skip attempting.
        if (isOwner) {
          const folderTargets: { id: string; label: string }[] = [
            { id: await ensurePicturesFolder(), label: "pictures" },
            { id: await ensureMapsFolder(), label: "maps" },
            { id: await ensureAssetsFolder(), label: "assets" },
            { id: await ensureFormsFolder(), label: "forms" },
          ];
          for (const email of added) {
            for (const { id, label } of folderTargets) {
              try {
                await shareFileWithUser(id, email);
              } catch (e) {
                manualShareNeeded = true;
                console.warn(`Could not auto-share the "${label}" folder (${id}) with ${email}; asking for a manual Drive share:`, e);
              }
            }
          }
          for (const email of removed) {
            for (const { id } of folderTargets) {
              try { await revokeFileAccess(id, email); } catch { /* best-effort revoke */ }
            }
          }
        } else if (added.length) {
          // Non-owner manager: folders must be shared by hand in Drive.
          manualShareNeeded = true;
        }
      }

      const fresh = await readMembers(spreadsheetId);
      setMembers(fresh);
      if (shareFailures.length) {
        const detail = shareFailures.map((f) => `${f.email} (${f.message})`).join("; ");
        notify(t("members.savedSharePartial", { emails: detail }), "info");
      } else if (manualShareNeeded) {
        const emails = added.join(", ");
        notify(t("members.savedShareRootNote", { emails }), "info");
        setDriveShareUrl(cachedRootFolderUrl() || "https://drive.google.com/drive");
      } else {
        notify(t("members.saved"));
      }
      setSnapshot(null);
      setLocked(true);
    } catch (e) {
      notifyError(t("members.saveError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

  return (
    <>
    {busy && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 select-none">
          <div className="relative">
            <img src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`} alt="MeW" className="w-16 h-16 opacity-90" />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      </div>
    )}
    <div className="flex justify-end px-4 pt-2">{slider}</div>
    <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
      <h2 className="font-bold text-xl flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-user-group text-pink-600" aria-hidden="true" />
        {t("members.title")}
      </h2>

      <div className="text-sm rounded-md px-3 py-2 flex items-start gap-2 border text-amber-800 bg-amber-50 border-amber-100">
        <i className="fa-solid fa-circle-info mt-0.5" aria-hidden="true" />
        <span>{isOwner ? t("members.introOwner") : t("members.intro")}</span>
      </div>

      {!isOwner && (
        <div className="text-sm rounded-md px-3 py-2 flex items-start gap-2 border text-gray-700 bg-gray-50 border-gray-200">
          <i className="fa-solid fa-circle-info mt-0.5" aria-hidden="true" />
          <span>{t("members.notOwnerNote")}</span>
        </div>
      )}

      {locked && (
        <div className="text-sm rounded-md px-3 py-2 flex items-center gap-2 border text-amber-800 bg-amber-50 border-amber-100">
          <i className="fa-solid fa-lock mt-0.5" aria-hidden="true" />
          <span>{t("members.locked")}</span>
        </div>
      )}

      {status && (
        <div className={`text-sm rounded-md px-3 py-2 flex items-start gap-2 border ${
          statusTone === "error" ? "text-red-700 bg-red-50 border-red-100"
          : statusTone === "success" ? "text-green-700 bg-green-50 border-green-100"
          : "text-gray-700 bg-gray-50 border-gray-200"}`}>
          <i className={`fa-solid mt-0.5 ${statusTone === "error" ? "fa-circle-exclamation" : statusTone === "success" ? "fa-circle-check" : "fa-circle-info"}`} aria-hidden="true" />
          <div className="space-y-1.5">
            <span>{status}</span>
            {driveShareUrl && (
              <a href={driveShareUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-blue-700 hover:underline">
                <i className="fa-brands fa-google-drive" aria-hidden="true" />
                <span>{t("members.openDriveFolder")}</span>
              </a>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="hidden sm:flex gap-2 px-1 text-xs font-medium text-gray-500">
          <span className="flex-1">{t("members.email")}</span>
          <span className="w-40">{t("members.role")}</span>
          <span className="w-9" />
        </div>
        {members.length === 0 && (
          <p className="text-sm text-gray-500 px-1 py-2">{t("members.empty")}</p>
        )}
        {members.map((m, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <input
              type="email"
              value={m.email}
              onChange={(e) => updateEmail(i, e.target.value)}
              disabled={locked}
              placeholder={t("members.emailPlaceholder")}
              aria-label={t("members.email")}
              className={`flex-1 ${inputCls}`}
            />
            <select
              value={m.role}
              onChange={(e) => updateRole(i, e.target.value as Member["role"])}
              disabled={locked}
              title={t("members.role")}
              aria-label={t("members.role")}
              className={`w-full sm:w-40 ${inputCls}`}
            >
              <option value="manager">{t("members.roleManager")}</option>
              <option value="fieldworker">{t("members.roleFieldworker")}</option>
            </select>
            <button
              type="button"
              onClick={() => removeRow(i)}
              disabled={locked}
              title={t("members.removeRow")}
              aria-label={t("members.removeRow")}
              className="inline-flex items-center justify-center w-full sm:w-9 h-9 rounded-md bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="fa-solid fa-trash" aria-hidden="true" />
            </button>
          </div>
        ))}

        {!locked && (
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900 px-1 py-1"
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
            <span>{t("members.addRow")}</span>
          </button>
        )}
      </div>

      {myEmail && (
        <p className="text-xs text-gray-500 px-1">{t("members.signedInAs", { email: myEmail })}</p>
      )}

      {locked ? (
        <button className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-md px-3 py-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors" onClick={startEdit} disabled={busy || !spreadsheetId}>
          <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
          <span>{t("members.edit")}</span>
        </button>
      ) : (
        <div className="flex gap-2">
          <button className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-md px-3 py-2 hover:bg-emerald-700 disabled:opacity-50 transition-colors" onClick={handleSave} disabled={busy || !spreadsheetId}>
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
            <span>{t("members.save")}</span>
          </button>
          <button className="inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-700 rounded-md px-3 py-2 hover:bg-gray-300 disabled:opacity-50 transition-colors" onClick={cancelEdit} disabled={busy}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
            <span>{t("members.cancel")}</span>
          </button>
        </div>
      )}

      {spreadsheetId && (
        <div className="text-xs text-gray-600 pt-2">
          <a href={spreadsheetUrl(spreadsheetId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline">
            <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
            <span>{t("sheet.openGoogleSheet")}</span>
          </a>
        </div>
      )}
    </div>
    </>
  );
}
